import { useAuthTransport } from "@magnit/util-auth/src/composables";
import type { $Fetch, FetchContext } from "ofetch";
import { getPlatformVersion, getUserAgent } from "../utils/clientInfo";
import {
  APP_COOKIE_STORAGE_KEYS,
  HEADERS_CONTENT_TYPE,
  HEADERS_USER_AGENT,
  HEADERS_PLATFORM_VERSION,
  HEADERS_DEVICE_TAG,
  HEADERS_FINGERPRINT,
  HEADERS_APP_VERSION,
  HEADERS_DEVISE_PLATFORM,
  HEADERS_NEW_MAGNIT,
  HEADERS_CLIENT_NAME,
} from "../constants";
import useAuthCookies from "../composables/useAuthCookies";

type ExtraFetchOptions = {
  gateway?: "web" | "magnit-id";
  permissions?: {
    jwt?: boolean;
  };
};

export default defineNuxtPlugin(() => {
  // эти штуки тут берём один раз в замыкание, чтобы на каждую отправку их не высчитывать
  // т.к. они практически не меняются
  // на SSR это тоже проблем вызвать не должно, т.к. набор плагинов на каждый входящий запрос инициализируется свой
  const userAgent = getUserAgent();
  const platformVersion = getPlatformVersion();
  const { public: publicConfig } = useRuntimeConfig();
  const appVersion = String(publicConfig.version || "");
  const clientName = String(publicConfig.clientName || "");
  const aud = String(publicConfig.aud || "");
  const deviceId = useCookie(APP_COOKIE_STORAGE_KEYS.uniqDeviceID).value || "omit empty";
  const ksid = useCookie(APP_COOKIE_STORAGE_KEYS.ksid).value || "disabled";
  const { cookieJWT } = useAuthCookies();
  const { authOnResponse, authOnRequest, retryOptions } = useAuthTransport({ aud, cookieJWT });

  const $transport = $fetch.create({
    ...retryOptions,
    async onRequest(ctx) {
      const { options } = ctx;
      const gateway = (options as ExtraFetchOptions).gateway ?? "web";
      options.baseURL = gateway === "web" ? "/webgate" : "/magnit-id";

      const headers = new Headers({
        [HEADERS_CONTENT_TYPE]: "application/json",
        [HEADERS_USER_AGENT]: userAgent,
        [HEADERS_PLATFORM_VERSION]: platformVersion,
        [HEADERS_DEVICE_TAG]: ksid,
        [HEADERS_FINGERPRINT]: deviceId,
        [HEADERS_APP_VERSION]: appVersion,
        [HEADERS_DEVISE_PLATFORM]: "Web",
        [HEADERS_NEW_MAGNIT]: "true",
        [HEADERS_CLIENT_NAME]: gateway === "web" ? clientName : "",
      });
      if ((options as ExtraFetchOptions).permissions?.jwt) {
        authOnRequest(ctx as FetchContext<any, any>);
      }
      // Для FormData не нужно явно задавать заголовок типа, ofetch сам его выведет и сделает multipart/form-data с нужным boundary
      // явно передадите content-type -- будет неверная отправка, т.к. boundary не будет вычислен
      if (options.body instanceof FormData) {
        headers.delete(HEADERS_CONTENT_TYPE);
      }
      // Правильно "спредить" заголовки хитрее чем кажется,
      // наивный спред не всегда сработает,
      // т.к. {...new Headers({'my-header': 'hello world'})} это будет не то что вы могли ожидать
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, name) => headers.set(name, value));
      } else if (options.headers) {
        (Array.isArray(options.headers)
          ? options.headers
          : Object.entries(options.headers)
        ).forEach(([name, value]) => headers.set(name, String(value)));
      }
      options.headers = headers;
    },
    async onResponse(ctx) {
      await authOnResponse(ctx as FetchContext<any, any>, $transport as $Fetch);
    },
    onRequestError(ctx) {
      try {
        console.error("request error", ctx.request, ctx.error);
      } catch (err) {
        console.error("При обработке `onRequestError` запроса произошла неизвестная ошибка.", err);
      }
    },
    onResponseError(ctx) {
      try {
        console.error(
          "response error",
          ctx.request,
          toValue(ctx.response?.status) ?? "Неизвестный статус",
          toValue(ctx.response?._data?.message) ?? "",
        );
      } catch (err) {
        console.error("При обработке `onResponseError` запроса произошла неизвестная ошибка.", err);
      }
    },
  });

  return {
    provide: {
      transport: $transport,
    },
  };
});
